// ─── Captura global de erros, com relatório copiável (só admin) ──────────────
//
// Apanha tudo o que corre mal e que de outra forma ficaria escondido na consola:
//   • erros de JavaScript não apanhados
//   • promessas rejeitadas sem catch
//   • erros de render do React (via ErrorBoundary)
//   • pedidos de rede falhados e respostas 5xx
//
// Mostra uma modal com o máximo de detalhe e um botão para copiar tudo de uma vez.
// Só aparece a administradores — o cliente nunca vê isto.
//
// ADITIVO: não altera comportamento nenhum já existente. As páginas que já tratam
// os seus erros continuam a trata-los à maneira delas; isto apanha o resto.

import React from 'react'
import { useAuth } from './AuthContext'
import { C } from './theme'
import { AlertTriangle, X, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react'

// ─── Barramento simples ──────────────────────────────────────────────────────
const subscritores = new Set()
let instalado = false

export function reportarErro(info) {
  const registo = {
    hora: new Date().toISOString(),
    pagina: (typeof window !== 'undefined' ? window.location.hash : '') || '(sem hash)',
    url: typeof window !== 'undefined' ? window.location.href : '',
    versao: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0',
    navegador: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    ...info,
  }
  subscritores.forEach(fn => { try { fn(registo) } catch { /* nunca rebentar aqui */ } })
}

function instalarHandlers() {
  if (instalado || typeof window === 'undefined') return
  instalado = true

  window.addEventListener('error', (ev) => {
    // Erros de carregamento de recursos (img, script) vêm sem ev.error
    if (!ev.error && ev.target && ev.target !== window) {
      reportarErro({
        tipo: 'Recurso não carregou',
        mensagem: `${ev.target.tagName || 'recurso'}: ${ev.target.src || ev.target.href || '(sem url)'}`,
      })
      return
    }
    reportarErro({
      tipo: 'Erro de JavaScript',
      mensagem: ev.message || String(ev.error),
      origem: ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : undefined,
      stack: ev.error?.stack,
    })
  }, true)

  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason
    reportarErro({
      tipo: 'Promessa rejeitada sem tratamento',
      mensagem: r?.message || String(r),
      stack: r?.stack,
    })
  })

  // Envolve o fetch para apanhar falhas de rede e erros do servidor. Só reporta
  // o que é mesmo anómalo: falha de ligação ou 5xx. Os 4xx costumam ser esperados
  // (401 de sessão expirada, 404, validações) e as páginas já os mostram.

  // Pedidos que o supabase-js faz sozinho em segundo plano e que ele próprio
  // repete. Falham sempre que o portátil adormece, o wi-fi oscila ou o
  // separador fica horas aberto — e recuperam sem ninguém fazer nada.
  // Mostrar uma modal vermelha por causa disto é assustar por nada: o
  // utilizador não provocou o erro nem tem como o resolver.
  // Pedidos de fundo que nao valem uma janela de erro. Falham sem
  // consequencia nenhuma para quem esta a usar a aplicacao, e a modal so
  // ensinaria a ignorar modais.
  //
  // O registo de passagem e o caso claro: serve para saber quando o cliente ca
  // esteve pela ultima vez, para o distintivo NOVO marcar o que apareceu desde
  // entao. Sem ele ha plano B — mostra-se o que foi anunciado nos ultimos dias.
  // E ha uma razao pratica: qualquer bloqueador de anuncios corta URLs com
  // "track" no nome, sem olhar ao que fazem. Por isso a funcao passou a chamar-
  // se registar-passagem, mas o nome antigo fica aqui por causa das versoes
  // antigas que ainda estejam em cache no browser de alguem.
  // O SINO DAS NOTIFICACOES E RUIDO DE FUNDO, mas so a LER.
  //
  // Corre de 30 em 30 segundos, todo o dia, em todos os ecras. E por isso o
  // pedido que apanha qualquer soluco da rede — e foi ele que levantou a janela
  // vermelha duas vezes em dois dias, a 13/09 as 23h38 e a 14/09 as 17h45. Nas
  // duas, o servidor estava bem trinta segundos depois; na segunda, o mesmo
  // timeout apanhou tambem uma consulta feita de outra maquina e de outra rede,
  // portanto foi a rota ate ao Supabase e nao o wi-fi de ninguem.
  //
  // Uma leitura que se repete sozinha daqui a meio minuto nao e uma avaria para
  // mostrar a alguem. Fica o aviso na consola, que e onde se investiga.
  //
  // ESCREVER continua a levantar a janela: marcar como lida ou apagar sao
  // accoes do utilizador, e se falharem ele tem de saber. Por isso a regra olha
  // ao metodo e nao so ao endereco.
  const ehRuidoDeFundo = (url, ehLeitura) =>
    /\/auth\/v1\/token/.test(url) ||
    /\/functions\/v1\/(track-visit|registar-passagem)/.test(url) ||
    (ehLeitura && /\/rest\/v1\/notificacoes/.test(url))

  // Só se repete o que é seguro repetir. Um GET pode ser pedido duas vezes sem
  // consequência; um POST/PATCH/DELETE não — repetir criaria uma segunda
  // encomenda ou reenviaria um email. Na dúvida sobre o método, não se repete.
  const podeRepetir = (args) => {
    const m = (args[1]?.method || (typeof args[0] === 'object' ? args[0]?.method : null) || 'GET').toUpperCase()
    return m === 'GET' || m === 'HEAD'
  }
  const esperar = (ms) => new Promise(r => setTimeout(r, ms))

  const fetchOriginal = window.fetch
  window.fetch = async function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '')
    try {
      const res = await fetchOriginal.apply(this, args)
      if (res.status >= 500) {
        let corpo = ''
        try { corpo = (await res.clone().text()).slice(0, 1000) } catch { /* ignora */ }
        reportarErro({
          tipo: `Erro do servidor (${res.status})`,
          mensagem: `${res.status} ${res.statusText} em ${url}`,
          detalhe: corpo || undefined,
        })
      }
      return res
    } catch (e) {
      // Sem rede não há erro nenhum para investigar — o browser está offline e
      // o pedido volta a correr quando a ligação regressar. Idem para os
      // pedidos automáticos de fundo. Fica o registo na consola, sem modal.
      const semRede = typeof navigator !== 'undefined' && navigator.onLine === false
      if (semRede || ehRuidoDeFundo(url, podeRepetir(args))) {
        console.warn('[LogicPRO] pedido falhou e vai ser repetido automaticamente:', url, e?.message || e)
        throw e
      }

      // Uma oscilação de rede de meio segundo não é uma avaria — é o que
      // acontece quando o wi-fi vacila ou o portátil acorda. Tenta-se outra
      // vez antes de incomodar seja quem for: se a segunda passar, não houve
      // problema nenhum a relatar. Só se falhar duas vezes seguidas é que
      // vale a pena mostrar a modal.
      if (podeRepetir(args)) {
        await esperar(700)
        try {
          const res2 = await fetchOriginal.apply(this, args)
          console.warn('[LogicPRO] pedido falhou à primeira e passou à segunda:', url)
          return res2
        } catch { /* falhou de novo — segue para o relatório */ }
      }

      reportarErro({
        tipo: 'Pedido de rede falhou',
        mensagem: `${e?.message || e} — ${url}`,
        detalhe: 'Causas típicas: a função do servidor morreu sem responder (o browser mostra isto como erro de CORS), sem ligação à internet, ou o pedido foi bloqueado.',
        stack: e?.stack,
      })
      throw e   // deixa o erro seguir — quem chamou continua a tratá-lo como antes
    }
  }
}

// ─── Texto do relatório, pronto a colar ──────────────────────────────────────
function montarTexto(e) {
  const linhas = [
    '--- ERRO LogicPRO ---',
    `Tipo:      ${e.tipo || '(desconhecido)'}`,
    `Mensagem:  ${e.mensagem || '(sem mensagem)'}`,
    e.origem   ? `Origem:    ${e.origem}` : null,
    `Página:    ${e.pagina}`,
    `URL:       ${e.url}`,
    `Hora:      ${e.hora}`,
    `Versão:    ${e.versao}`,
    `Navegador: ${e.navegador}`,
    e.componente ? `\nComponente React:\n${e.componente}` : null,
    e.detalhe    ? `\nDetalhe:\n${e.detalhe}` : null,
    e.stack      ? `\nStack:\n${e.stack}` : null,
    '--- fim ---',
  ]
  return linhas.filter(Boolean).join('\n')
}

// ─── Modal ───────────────────────────────────────────────────────────────────
export default function ErroGlobal() {
  const { isAdmin } = useAuth()
  const [erro, setErro] = React.useState(null)
  const [copiado, setCopiado] = React.useState(false)
  const [verStack, setVerStack] = React.useState(false)

  React.useEffect(() => {
    instalarHandlers()
    const fn = (registo) => { setErro(registo); setCopiado(false); setVerStack(false) }
    subscritores.add(fn)
    return () => subscritores.delete(fn)
  }, [])

  if (!isAdmin || !erro) return null

  const texto = montarTexto(erro)
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2500)
    } catch {
      // Sem permissão para a área de transferência: selecciona o texto para
      // o utilizador poder copiar à mão com Ctrl+C
      const el = document.getElementById('erro-global-texto')
      if (el) {
        const r = document.createRange(); r.selectNodeContents(el)
        const s = window.getSelection(); s.removeAllRanges(); s.addRange(r)
      }
    }
  }

  const linhaStyle = { display: 'grid', gridTemplateColumns: '92px 1fr', gap: '6px', fontSize: '12px', padding: '3px 0' }
  const rotulo = { color: C.textMuted, fontWeight: '700' }
  const valor = { color: C.text, wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) setErro(null) }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: C.surface, borderRadius: '8px', maxWidth: '760px', width: '100%', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.3)', border: `2px solid ${C.danger}` }}>

        <div style={{ padding: '14px 18px', background: C.dangerSoft, borderBottom: `1px solid ${C.danger}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
            <AlertTriangle size={19} color={C.danger} style={{ flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: C.danger }}>{erro.tipo || 'Erro'}</p>
              <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#7c2d12' }}>Só tu vês esta janela. Copia e cola no Claude.</p>
            </div>
          </div>
          <button onClick={() => setErro(null)} title="Fechar"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: '4px', flexShrink: 0 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '14px 18px', overflowY: 'auto' }}>
          <p style={{ margin: '0 0 12px', padding: '10px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '5px', fontSize: '13px', fontWeight: '700', color: C.text, wordBreak: 'break-word' }}>
            {erro.mensagem || '(sem mensagem)'}
          </p>

          <div style={linhaStyle}><span style={rotulo}>Página</span><span style={valor}>{erro.pagina}</span></div>
          {erro.origem && <div style={linhaStyle}><span style={rotulo}>Origem</span><span style={valor}>{erro.origem}</span></div>}
          <div style={linhaStyle}><span style={rotulo}>Hora</span><span style={valor}>{new Date(erro.hora).toLocaleString('pt-PT')}</span></div>
          <div style={linhaStyle}><span style={rotulo}>Versão</span><span style={valor}>{erro.versao}</span></div>

          {erro.detalhe && (
            <div style={{ marginTop: '10px', padding: '10px 12px', background: C.warningSoft, border: `1px solid ${C.warning}`, borderRadius: '5px', fontSize: '12px', color: '#7c2d12', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {erro.detalhe}
            </div>
          )}

          {(erro.stack || erro.componente) && (
            <>
              <button onClick={() => setVerStack(v => !v)}
                style={{ marginTop: '12px', border: 'none', background: 'transparent', cursor: 'pointer', color: C.primary, fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px', padding: 0 }}>
                {verStack ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Detalhe técnico
              </button>
              {verStack && (
                <pre style={{ marginTop: '8px', padding: '10px 12px', background: '#0a0a0a', color: '#e4e4e7', borderRadius: '5px', fontSize: '11px', lineHeight: '1.5', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {[erro.componente, erro.stack].filter(Boolean).join('\n\n')}
                </pre>
              )}
            </>
          )}

          {/* Texto completo, escondido — serve de alvo caso a cópia automática falhe */}
          <pre id="erro-global-texto" style={{ position: 'absolute', left: '-9999px', whiteSpace: 'pre-wrap' }}>{texto}</pre>
        </div>

        <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, background: C.bg, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={() => setErro(null)}
            style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
            Fechar
          </button>
          <button onClick={copiar}
            style={{ height: '36px', padding: '0 16px', border: 'none', background: copiado ? C.success : C.primary, color: '#fff', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {copiado ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar relatório</>}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Barreira de render ──────────────────────────────────────────────────────
// Um erro dentro de um componente React deixaria o ecrã em branco. Aqui o erro é
// reportado à modal e mostra-se uma mensagem em vez da página vazia.
export class ErroBoundary extends React.Component {
  constructor(props) { super(props); this.state = { rebentou: false } }
  static getDerivedStateFromError() { return { rebentou: true } }
  componentDidCatch(erro, info) {
    reportarErro({
      tipo: 'Erro ao desenhar a página',
      mensagem: erro?.message || String(erro),
      stack: erro?.stack,
      componente: info?.componentStack,
    })
  }
  render() {
    if (!this.state.rebentou) return this.props.children
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center', color: C.textMuted }}>
        <AlertTriangle size={44} color={C.danger} style={{ margin: '0 auto 12px' }} />
        <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: C.text }}>Esta página não conseguiu abrir</h2>
        <p style={{ margin: '6px 0 16px', fontSize: '13px' }}>O detalhe do erro está na janela que apareceu.</p>
        <button onClick={() => this.setState({ rebentou: false })}
          style={{ height: '36px', padding: '0 16px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '5px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
          Tentar de novo
        </button>
      </div>
    )
  }
}
